import React from "react";

import type { IIssue, ISiteSlim, IResponseWithLoading } from "App/Modules/Models";
import { IssueType, IssueSeverity } from "App/Modules/Models";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import type { IconProp } from "@fortawesome/fontawesome-svg-core";
import { faHeartbeat, faImage, faChartNetwork, faCheck, faCircleNotch, faCogs } from "@fortawesome/pro-light-svg-icons";
import { faCheckCircle, faInfoSquare } from "@fortawesome/pro-solid-svg-icons";

import { Avatar, Typography } from "@material-ui/core";
import type { TFunction } from "i18next";
import moment from "moment-timezone";
// import { StyledDuration, formatDateTime, getUserProfile } from "App/ReactComponents/Shared";
import { StyledDuration, formatDateTime, getUserProfile } from "App/ReactComponents/Shared";
import { ButtonWithIcon } from "../../Shared/Components/Buttons";
import type { LocalIssueRowState } from "./IssueTypes";
import { useIssueStyles } from "./issueListStyles";

const getIcon = (icon: IconProp) => <FontAwesomeIcon icon={icon} fixedWidth />;

const getIssueIcon = (issue: IIssue) => {
    switch (issue.issueType) {
        case IssueType.NoHeartbeatReceived:
            return getIcon(faHeartbeat);
        case IssueType.NoImageReceived:
            return getIcon(faImage);
        case IssueType.NoImageFromCamera:
        case IssueType.ConnectionFailure:
        case IssueType.ConnectionDegraded:
            return getIcon(faChartNetwork);
        case IssueType.SystemFailure:
            return getIcon(faCogs);
        case IssueType.Tampering:
        case IssueType.VideoQualityFailure:
            return getIcon(faImage);
        default:
            return "";
    }
};

const getIssueAvatarClass = (issue: IIssue, classes: ReturnType<typeof useIssueStyles>) => {
    if (!issue.isActive) {
        return classes["issueResolvedAvatar"];
    }
    switch (issue.severity) {
        case IssueSeverity.Error:
            return "error";
        case IssueSeverity.Warning:
            return "warning";
        default:
            return "other";
    }
};

export const getIssueAvatar = (issue: IIssue, classes: ReturnType<typeof useIssueStyles>) => (
    <Avatar className={getIssueAvatarClass(issue, classes)}>{getIssueIcon(issue)}</Avatar>
);

const issueCellPrimaryRow = (issue: IIssue, t: TFunction) => (
    <div className="nowrap">
        <Typography variant="body1" color="textPrimary">
            {issue.source.name}
        </Typography>
        <Typography variant="body2" color="textSecondary">
            , {t(`Enums.IssueSourceType.${issue.source.typeName}`)}
        </Typography>
    </div>
);

const issueCellSecondaryRow = (issue: IIssue, t: TFunction) => {
    return (
        <div className="nowrap">
            <Typography variant="body2">
                {`
                    ${t(`Enums.IssueTypeEnum.${issue.issueTypeName}`)} -
                    ${t(`Enums.IssueSubTypeEnum.${issue.issueSubTypeName}`)}
                `}
            </Typography>
        </div>
    );
};

const issueResolvedRow = (issue: IIssue, t: TFunction, classes: ReturnType<typeof useIssueStyles>, site: ISiteSlim) => {
    const resolvedOnDate = issue.dtResolved ? formatDateTime(issue.dtResolved, site.timeZone) : null;
    let zoneMismatch = false;
    if (issue.issueSubTypeName === "Tampering_Redirected") {
        zoneMismatch = true;
    }
    return (
        !issue.isActive && (
            <div className={classes["issueCell"]}>
                {zoneMismatch ? (
                    <React.Fragment>
                        <div className={classes["issueResolvedPrimaryRowAlt"]}>
                            <FontAwesomeIcon icon={faInfoSquare} />
                            <Typography variant="caption">{t("Entities.Issue.ResolvedPotentialMismatch")}</Typography>
                        </div>
                        <div className="nowrap">
                            <Typography variant="caption">{t("Entities.Issue.ResolvedZoneAffected")}</Typography>
                        </div>
                    </React.Fragment>
                ) : (
                    <React.Fragment>
                        <div className={classes["issueResolvedPrimaryRow"]}>
                            <FontAwesomeIcon icon={faCheckCircle} />
                            <Typography variant="caption">
                                {`${t("Entities.Issue.ResolvedOn")} ${resolvedOnDate}`}
                            </Typography>
                        </div>
                        <div className="nowrap">
                            <Typography variant="caption">
                                <StyledDuration durationSeconds={issue.issueDurationSeconds} />
                            </Typography>
                        </div>
                    </React.Fragment>
                )}
            </div>
        )
    );
};

export const issueCell = (issue: IIssue, classes: ReturnType<typeof useIssueStyles>, t: TFunction, site: ISiteSlim) => (
    <div className={classes["issueCell"]}>
        {issueCellPrimaryRow(issue, t)}
        {issueCellSecondaryRow(issue, t)}
        {issueResolvedRow(issue, t, classes, site)}
    </div>
);

type IssueAcknowledgedCellProps = {
    issue: IIssue;
    loadingState: IResponseWithLoading<void>;
    localState: LocalIssueRowState;
    timeZone: string;
    updateIssue: (issue: Partial<IIssue>, state: LocalIssueRowState) => void;
    t: TFunction;
};

export const IssueAcknowledgedCell = (props: IssueAcknowledgedCellProps) => {
    const profile = getUserProfile();
    const classes = useIssueStyles();
    const { t } = props;

    const { isAcknowledged } = props.issue;
    const isLocallyAcknowledged = props.localState?.isAcknowledged;
    const isLoading = props.loadingState.isLoading || props.loadingState.isPartiallyLoading;
    const hasError = props.loadingState.hasError || props.loadingState.hasPartialError;

    return isAcknowledged ? (
        <>
            <div className={classes["acknowledgedPrimaryRow"]}>
                {`${t("Entities.Issue.AcknowledgedBy")} ${props.issue.acknowledgedBy?.name}`}
                <span className={classes["tooltip"]}>{props.issue.acknowledgedBy?.name}</span>
            </div>
            <div className={classes["acknowledgedSecondaryRow"]}>
                <Typography variant="body2">{formatDateTime(props.issue.dtAcknowledged, props.timeZone)}</Typography>
            </div>
        </>
    ) : (
        <>
            <ButtonWithIcon
                startIcon={isLoading && isLocallyAcknowledged ? faCircleNotch : faCheck}
                startIconProps={isLoading && isLocallyAcknowledged ? { spin: true } : {}}
                variant="contained"
                className={hasError && isLocallyAcknowledged && classes["acknowledgeButtonHasError"]}
                size="small"
                fullWidth
                onClick={() =>
                    props.updateIssue(
                        {
                            id: props.issue.id,
                            isAcknowledged: true,
                            acknowledgedBy: { id: "", name: profile.fullName },
                        },
                        {
                            isAcknowledged: true,
                            acknowledgedBy: { id: "", name: profile.fullName },
                            dtAcknowledged: moment(),
                        },
                    )
                }
            >
                {props.t("Gui.Buttons.Acknowledge")}
            </ButtonWithIcon>
        </>
    );
};
